import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from '@flowshare/protocol';

import { bytesToBase64Url } from './base64url.js';

export type RandomInt = (maxExclusive: number) => number;

export function randomBytes(length: number): Uint8Array {
  if (!Number.isInteger(length) || length <= 0) {
    throw new Error('randomBytes requires a positive integer length');
  }
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * Cryptographically secure integer in [0, maxExclusive).
 * Uses rejection sampling to avoid modulo bias for arbitrary ranges.
 */
export function randomInt(maxExclusive: number): number {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > 0xffffffff) {
    throw new Error('randomInt requires an integer in [1, 2^32)');
  }

  const limit = Math.floor(0x100000000 / maxExclusive) * maxExclusive;
  const buffer = new Uint32Array(1);
  let value = 0;

  do {
    crypto.getRandomValues(buffer);
    value = buffer[0] ?? 0;
  } while (value >= limit);

  return value % maxExclusive;
}

export function randomHex(byteLength: number): string {
  return [...randomBytes(byteLength)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/** 16 random bytes as 32 lowercase hex characters. */
export function createTransferId(): string {
  return randomHex(16);
}

export function createPeerId(): string {
  return `p_${randomHex(8)}`;
}

/** Internal, opaque room identifier. Never leaves the Durable Object. */
export function createRoomId(): string {
  return `rm_${bytesToBase64Url(randomBytes(18))}`;
}

/** 32-byte bearer token proving room ownership. */
export function createHostToken(): string {
  return bytesToBase64Url(randomBytes(32));
}

/**
 * Six character room code. 32^6 possibilities; 32 divides 2^32 so a plain
 * modulo would be unbiased, but we reuse rejection sampling for clarity.
 */
export function createRoomCode(nextInt: RandomInt = randomInt): string {
  let code = '';
  for (let index = 0; index < ROOM_CODE_LENGTH; index += 1) {
    code += ROOM_CODE_ALPHABET[nextInt(ROOM_CODE_ALPHABET.length)] ?? '';
  }
  return code;
}

/** Generate an idempotency-friendly request id for client → server messages. */
export function createRequestId(): string {
  return randomHex(8);
}
